import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  key: 0,
  "data-preview-error": "",
  role: "status",
  class: "mb-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800"
}
const _hoisted_2 = {
  "data-next-dates": "",
  class: "mt-4"
}
const _hoisted_3 = { class: "m-0 mb-2 text-xs font-semibold text-gray-500" }
const _hoisted_4 = { class: "flex flex-wrap gap-1.5" }
const _hoisted_5 = { class: "m-0 mt-2 text-xs text-gray-500" }

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import WizardConfirmModal from '@/components/neue/wizard/WizardConfirmModal.vue'
import WizardNav from '@/components/neue/wizard/WizardNav.vue'
import WizardShell from '@/components/neue/wizard/WizardShell.vue'
import { useConfirm } from '@/composables/useConfirm'
import { useFulfillmentWizard } from '@/composables/useFulfillmentWizard'
import { provideHighlightTerms } from '@/composables/useHighlightTerms'
import { useToast } from '@/composables/useToast'
import { provideWizard } from '@/composables/useWizardContext'
import { methodDraft } from '@/composables/useWizardDraft'
import {
  methodLabel,
  NEXT_DATES_HINT,
  NEXT_DATES_LABELS,
  QUESTION_STEPS,
  stepHeading,
  stepSubtitle,
  WIZARD_STEPS
} from '@/config/fulfillmentWizardSteps'
import { describeSetup, nextDateChips, whatWeSetUp } from '@/helpers/fulfillmentWizardSchedule'
import { railRows } from '@/helpers/wizardRail'
import api from '@/store/api'
import { mapType } from '@/store/mappers'
import StepDays from '@/view/store/fulfillmentWizard/steps/StepDays.vue'
import StepMethod from '@/view/store/fulfillmentWizard/steps/StepMethod.vue'
import StepOrdering from '@/view/store/fulfillmentWizard/steps/StepOrdering.vue'
import StepPricing from '@/view/store/fulfillmentWizard/steps/StepPricing.vue'
import StepReview from '@/view/store/fulfillmentWizard/steps/StepReview.vue'
import StepStores from '@/view/store/fulfillmentWizard/steps/StepStores.vue'
import StepTimes from '@/view/store/fulfillmentWizard/steps/StepTimes.vue'
import StepWhere from '@/view/store/fulfillmentWizard/steps/StepWhere.vue'

const STORES = 'stores?included=fulfillment_method_store_pairs'
const ENTER_OWNERS = 'textarea, button, a, [role="button"], [role="listbox"], select'


export default {
  __name: 'FulfillmentMethodWizard',
  setup(__props, { expose: __expose }) {

const route = useRoute()
const router = useRouter()
const store = useStore()
const { showToast } = useToast()

const wizard = useFulfillmentWizard({ storeId: route.query.store ?? null })
provideWizard(wizard)
const stepKey = computed(() => wizard.stepKey.value)
const onReview = computed(() => stepKey.value === 'review')
const stores = computed(() => mapType('store'))
const currency = computed(() => store.state.loggedInUser?.attributes?.currency || 'USD')

const title = computed(() => wizard.data.name.trim() || 'New fulfillment method')
methodDraft.follow(() => wizard.data.name.trim())
const heading = computed(() => stepHeading(stepKey.value, wizard.data))
const subtitle = computed(() => stepSubtitle(stepKey.value, wizard.data))
const position = computed(() => `Step ${wizard.step.value.index + 1} of ${QUESTION_STEPS}`)
const eyebrow = computed(() => (onReview.value ? 'Review' : `${position.value} · ${wizard.step.value.label}`))
const railButtonLabel = computed(() => (onReview.value ? 'Setup' : position.value))
const nextStep = computed(() => WIZARD_STEPS[wizard.step.value.index + 1])
const primaryLabel = computed(() => {
  if (onReview.value) return 'Create fulfillment method'
  return nextStep.value.key === 'review' ? 'Review' : `Continue to ${nextStep.value.label}`
})

const PREVIEW_ERROR_STEPS = ['stores', 'days', 'times', 'ordering', 'review']
const previewErrorShown = computed(
  () => Boolean(wizard.previewError.value) && PREVIEW_ERROR_STEPS.includes(stepKey.value)
)
const nextDates = computed(() => {
  const chips = nextDateChips(wizard.preview.value)
  return chips.length ? chips : [{ label: 'No dates yet', closed: false }]
})
const nextDatesLabel = computed(() => NEXT_DATES_LABELS[wizard.data.type] ?? 'Next dates')
const storeNames = computed(() => stores.value.map(s => ({ id: s.id, name: s.attributes.store_name })))
const copyContext = computed(() => ({
  stores: storeNames.value,
  country: store.getters.country,
  currency: currency.value
}))
const description = computed(() => describeSetup(wizard.data, copyContext.value))
const setupItems = computed(() => whatWeSetUp(wizard.data, copyContext.value))

const stepsThrough = key => WIZARD_STEPS.slice(0, WIZARD_STEPS.findIndex(s => s.key === key) + 1).map(s => s.key)
const visited = ref(new Set())
watch(stepKey, key => (visited.value = new Set([...visited.value, ...stepsThrough(key)])), { immediate: true })
const rail = computed(() => railRows(description.value.rows, { currentKey: stepKey.value, visited: visited.value }))

provideHighlightTerms(
  computed(() => [wizard.data.name.trim(), wizard.data.address.address1.trim(), ...storeNames.value.map(s => s.name)])
)

const canCreate = computed(
  () => wizard.canReview.value && wizard.previewOk.value && wizard.occurrences.value.length > 0
)

const create = async () => {
  if (!canCreate.value || wizard.saving.value) return
  const id = await wizard.submit()
  if (!id) return
  showToast('Fulfillment method created')
  router.push(`/store/fulfillment-methods/${id}`)
}

const advance = () => (onReview.value ? create() : wizard.next())

const storesLoaded = ref(false)

const syncStores = () => {
  const known = stores.value.map(s => String(s.id))
  const kept = wizard.data.storeIds.filter(id => known.includes(id))
  if (kept.length !== wizard.data.storeIds.length) wizard.data.storeIds = kept
  if (known.length === 1 && !kept.length) wizard.data.storeIds = [known[0]]
  wizard.reopenUnfinished()
}

const loadStores = () =>
  api.call('GET', STORES).then(
    () => true,
    () => false
  )

const { state: confirmState, ask, confirm: confirmYes, cancel: confirmNo } = useConfirm()

const startOver = async () => {
  const sure = await ask({
    title: 'Start over?',
    message: `Everything entered for ${methodLabel(wizard.data)} is cleared. Nothing has been saved yet.`,
    confirmLabel: 'Start over',
    danger: true
  })
  if (!sure) return
  wizard.reset()
  visited.value = new Set(stepsThrough('method'))
  if (storesLoaded.value) syncStores()
}

const cancel = async () => {
  const sure =
    wizard.pristine() ||
    (await ask({
      title: 'Discard this method?',
      message: `Nothing has been saved, so everything entered for ${methodLabel(wizard.data)} is lost.`,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      danger: true
    }))
  if (!sure) return
  wizard.reset()
  router.push('/store/fulfillment-methods')
}

const onKeydown = event => {
  if (event.key !== 'Enter' || event.repeat || event.defaultPrevented) return
  if (event.target?.closest?.(ENTER_OWNERS)) return
  event.preventDefault()
  advance()
}

onMounted(async () => {
  window.addEventListener('keydown', onKeydown)
  storesLoaded.value = stores.value.length > 0 || (await loadStores())
  if (storesLoaded.value) syncStores()
})
watch(
  () => stores.value.map(s => s.id).join(','),
  () => storesLoaded.value && syncStores()
)
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

__expose({ wizard })

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(WizardShell, {
      title: title.value,
      "crumb-label": "Fulfillment Methods",
      "crumb-to": "/store/fulfillment-methods",
      "crumb-current": "New method",
      "draft-label": "Draft · not saved",
      eyebrow: eyebrow.value,
      heading: heading.value,
      subtitle: subtitle.value,
      "rail-rows": rail.value,
      "rail-title": "Setup",
      "rail-button-label": railButtonLabel.value,
      onCancel: cancel,
      onJump: _unref(wizard).go
    }, {
      banner: _withCtx(() => [
        (previewErrorShown.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_unref(wizard).previewError.value), 1))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(WizardNav, {
          "show-back": _unref(wizard).step.value.index > 0,
          "primary-label": primaryLabel.value,
          "primary-enabled": onReview.value ? canCreate.value : _unref(wizard).valid.value,
          loading: _unref(wizard).saving.value,
          arrow: !onReview.value,
          onBack: _unref(wizard).back,
          onPrimary: advance
        }, null, 8, ["show-back", "primary-label", "primary-enabled", "loading", "arrow", "onBack"])
      ]),
      "rail-foot": _withCtx(() => [
        _cache[0] || (_cache[0] = _createElementVNode("p", { class: "m-0 text-[13px] text-gray-500" }, "Nothing is saved until you press Create fulfillment method.", -1)),
        _createElementVNode("button", {
          type: "button",
          class: "mt-2 text-[13px] text-gray-600 underline hover:text-gray-900",
          onClick: startOver
        }, " Start over "),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(nextDatesLabel.value), 1),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nextDates.value, (chip) => {
              return (_openBlock(), _createElementBlock("span", {
                key: chip.label,
                class: _normalizeClass(["rounded-full px-2 py-px text-xs font-medium", chip.closed ? 'bg-[#f3f4f6] text-gray-500 line-through' : 'bg-[#ecfdf5] text-[#065f46]'])
              }, _toDisplayString(chip.label), 3))
            }), 128))
          ]),
          _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(NEXT_DATES_HINT)), 1)
        ])
      ]),
      default: _withCtx(() => [
        (stepKey.value === 'method')
          ? (_openBlock(), _createBlock(StepMethod, { key: 0 }))
          : (stepKey.value === 'where')
            ? (_openBlock(), _createBlock(StepWhere, { key: 1 }))
            : (stepKey.value === 'stores')
              ? (_openBlock(), _createBlock(StepStores, {
                  key: 2,
                  stores: stores.value
                }, null, 8, ["stores"]))
              : (stepKey.value === 'days')
                ? (_openBlock(), _createBlock(StepDays, { key: 3 }))
                : (stepKey.value === 'times')
                  ? (_openBlock(), _createBlock(StepTimes, { key: 4 }))
                  : (stepKey.value === 'ordering')
                    ? (_openBlock(), _createBlock(StepOrdering, { key: 5 }))
                    : (stepKey.value === 'pricing')
                      ? (_openBlock(), _createBlock(StepPricing, { key: 6 }))
                      : (_openBlock(), _createBlock(StepReview, {
                          key: 7,
                          description: description.value,
                          setup: setupItems.value,
                          errors: _unref(wizard).errors.value
                        }, null, 8, ["description", "setup", "errors"]))
      ]),
      _: 1
    }, 8, ["title", "eyebrow", "heading", "subtitle", "rail-rows", "rail-button-label", "onJump"]),
    _createVNode(WizardConfirmModal, _mergeProps(_unref(confirmState), {
      onConfirm: _unref(confirmYes),
      onCancel: _unref(confirmNo)
    }), null, 16, ["onConfirm", "onCancel"])
  ], 64))
}
}

}